import { headingId } from '@/modules/docs/markdown-parse';

/**
 * The blog's Markdown, as blocks.
 *
 * ## A superset of the documentation's, kept apart from it
 *
 * The documentation parser reads headings, paragraphs, lists and fenced code, and that is all its
 * pages use. A post also wants a table, a figure with a caption and a callout. Teaching the
 * documentation parser about them would change how every guide is read for the sake of a dozen
 * posts, so the posts have their own, which agrees with it about everything they share, down to
 * the anchor a heading gets.
 *
 * Nothing here produces HTML. Every piece of text becomes a text node when it is drawn, so a post
 * cannot inject markup, and there is no sanitiser to get wrong.
 */

export type BlogBlock =
  | { type: 'heading'; level: 2 | 3; text: string; id: string }
  | { type: 'paragraph'; text: string }
  | { type: 'list'; ordered: boolean; items: string[] }
  | { type: 'code'; language: string; text: string }
  | { type: 'callout'; paragraphs: string[] }
  | { type: 'table'; header: string[]; rows: string[][] }
  | { type: 'figure'; alt: string; src: string; caption: string };

/** `![alt text](/blog/file.svg "Caption")` on a line of its own. The caption is optional. */
const FIGURE = /^!\[([^\]]*)\]\(([^)\s]+)(?:\s+"([^"]*)")?\)$/;

/** `| --- | :---: |`, the line under a table's header. */
const SEPARATOR = /^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?$/;

function cells(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim());
}

export function parseBlogBlocks(markdown: string): BlogBlock[] {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const blocks: BlogBlock[] = [];
  let paragraph: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;

  const flush = (): void => {
    if (paragraph.length > 0) {
      blocks.push({ type: 'paragraph', text: paragraph.join(' ') });
      paragraph = [];
    }

    if (list !== null) {
      blocks.push({ type: 'list', ordered: list.ordered, items: list.items });
      list = null;
    }
  };

  for (let index = 0; index < lines.length; index++) {
    const line = lines[index] ?? '';

    if (line.startsWith('```')) {
      flush();
      const language = line.slice(3).trim();
      const code: string[] = [];
      index++;

      while (index < lines.length && !(lines[index] ?? '').startsWith('```')) {
        code.push(lines[index] ?? '');
        index++;
      }

      blocks.push({ type: 'code', language, text: code.join('\n') });
      continue;
    }

    const heading = /^(#{2,3})\s+(.*)$/.exec(line);

    if (heading !== null) {
      flush();
      const text = (heading[2] ?? '').trim();
      blocks.push({ type: 'heading', level: heading[1] === '##' ? 2 : 3, text, id: headingId(text) });
      continue;
    }

    const figure = FIGURE.exec(line.trim());

    if (figure !== null) {
      flush();
      blocks.push({ type: 'figure', alt: figure[1] ?? '', src: figure[2] ?? '', caption: figure[3] ?? '' });
      continue;
    }

    if (line.startsWith('>')) {
      flush();
      const quoted: string[] = [];

      while (index < lines.length && (lines[index] ?? '').startsWith('>')) {
        quoted.push((lines[index] ?? '').replace(/^>\s?/, ''));
        index++;
      }

      // The loop's own increment moves past the last quoted line.
      index--;

      const paragraphs: string[] = [];
      let current: string[] = [];

      for (const text of quoted) {
        if (text.trim() === '') {
          if (current.length > 0) {
            paragraphs.push(current.join(' '));
            current = [];
          }
        } else {
          current.push(text.trim());
        }
      }

      if (current.length > 0) {
        paragraphs.push(current.join(' '));
      }

      blocks.push({ type: 'callout', paragraphs });
      continue;
    }

    if (line.startsWith('|') && SEPARATOR.test((lines[index + 1] ?? '').trim())) {
      flush();
      const header = cells(line);
      const rows: string[][] = [];
      index += 2;

      while (index < lines.length && (lines[index] ?? '').startsWith('|')) {
        rows.push(cells(lines[index] ?? ''));
        index++;
      }

      index--;
      blocks.push({ type: 'table', header, rows });
      continue;
    }

    const bullet = /^\s*-\s+(.*)$/.exec(line);
    const numbered = /^\s*\d+\.\s+(.*)$/.exec(line);

    if (bullet !== null || numbered !== null) {
      const ordered = numbered !== null;

      if (paragraph.length > 0) {
        blocks.push({ type: 'paragraph', text: paragraph.join(' ') });
        paragraph = [];
      }

      if (list === null || list.ordered !== ordered) {
        if (list !== null) {
          blocks.push({ type: 'list', ordered: list.ordered, items: list.items });
        }
        list = { ordered, items: [] };
      }

      list.items.push(((bullet ?? numbered)?.[1] ?? '').trim());
      continue;
    }

    if (line.trim() === '') {
      flush();
      continue;
    }

    // A line indented under a list item continues it.
    if (list !== null && /^\s{2,}\S/.test(line)) {
      list.items[list.items.length - 1] += ' ' + line.trim();
      continue;
    }

    if (list !== null) {
      blocks.push({ type: 'list', ordered: list.ordered, items: list.items });
      list = null;
    }

    paragraph.push(line.trim());
  }

  flush();

  return blocks;
}

/** Markdown syntax removed, so a count or an excerpt reads what a person would. */
export function plainText(text: string): string {
  return text
    .replace(/\[([^\]]+)\]\([^)\s]+\)/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1');
}

/**
 * Words in the prose and words in the code, counted apart.
 *
 * A reader skims a command and reads a paragraph, so reading time is worked out from the prose
 * alone; the code is reported so a post that is mostly commands is noticed.
 */
export function countWords(blocks: BlogBlock[]): { prose: number; code: number } {
  const count = (text: string): number =>
    plainText(text)
      .split(/\s+/)
      .filter((word) => /[\p{L}\p{N}]/u.test(word)).length;

  let prose = 0;
  let code = 0;

  for (const block of blocks) {
    switch (block.type) {
      case 'heading':
      case 'paragraph':
        prose += count(block.text);
        break;
      case 'list':
        prose += block.items.reduce((sum, item) => sum + count(item), 0);
        break;
      case 'callout':
        prose += block.paragraphs.reduce((sum, text) => sum + count(text), 0);
        break;
      case 'table':
        prose += [block.header, ...block.rows].flat().reduce((sum, cell) => sum + count(cell), 0);
        break;
      case 'code':
        code += count(block.text);
        break;
      case 'figure':
        break;
    }
  }

  return { prose, code };
}
